import { timeZones } from "@/lib/constants";
import {
  country_convert_reverse,
  removeEmojis,
} from "@/lib/utils/locationUtils";
import { normalizeTestGoal } from "@/lib/utils/testGoal";
import { parseGoalMetrics, type GoalMetrics, type ResultsGoal } from "@/lib/experiments/goalMetric";
import { interceptDemoAPI, isDemoSite, demoGenerators } from "@/lib/utils/demoData";
import { reportOnboardingScriptNotDetected } from "@/lib/observability/onboarding";
import type {
  GeoRestriction,
  AudienceRestriction,
} from "@/lib/types/experiment-restrictions";

const PUBLIC_IS_PROD = import.meta.env.VITE_PUBLIC_IS_PROD;
const PUBLIC_IS_LOCAL = import.meta.env.VITE_PUBLIC_IS_LOCAL;

const PUBLIC_API_URL = import.meta.env.VITE_PUBLIC_API_URL;

const API_URL = PUBLIC_API_URL
  ? `${PUBLIC_API_URL}/api/v1/`
  : PUBLIC_IS_PROD === "TRUE"
  ? `https://app.humblytics.com/api/v1/`
  : PUBLIC_IS_LOCAL === "TRUE"
  ? `http://localhost:3000/api/v1/`
  : `https://humblytics.onrender.com/api/v1/`;

/** Base for hooks that call `fetch` directly; a relative `/api/v1/` hits the SPA host and returns index.html. */
export const API_V1_URL = API_URL;

export class ApiError extends Error {
  status: number;
  body: unknown;

  constructor(status: number, body: unknown, message?: string) {
    super(message || `HTTP error! status: ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

export const parseResponseBody = async (response: Response): Promise<unknown> => {
  try {
    const contentType = response.headers.get("content-type") || "";
    if (contentType.includes("application/json")) {
      return await response.json();
    }

    const text = await response.text();
    return text || null;
  } catch {
    return null;
  }
};

const getApiErrorMessage = (status: number, body: unknown) => {
  if (body && typeof body === "object") {
    const data = body as Record<string, unknown>;
    if (typeof data.message === "string") return data.message;
    if (typeof data.error === "string") return data.error;
  }

  return `HTTP error! status: ${status}`;
};

export const createApiError = async (response: Response) => {
  const body = await parseResponseBody(response);
  return new ApiError(response.status, body, getApiErrorMessage(response.status, body));
};

const getErrorBodyCode = (body: unknown): string | null => {
  if (!body || typeof body !== "object") return null;
  const data = body as Record<string, unknown>;
  return typeof data.error === "string"
    ? data.error
    : typeof data.code === "string"
      ? data.code
      : null;
};

// Rate limiting / throttling is a transient 429, not a plan ceiling — its codes
// ("rate_limit", "rate_limited") contain the "limit" substring, so they must be
// excluded before any "limit"-based plan detection. Same for input-size caps
// ("character_limit", "length_limit").
const NON_PLAN_LIMIT_CODE = /rate.?limit|throttl|too.?many|character.?limit|char.?limit|length.?limit|size.?limit/;

// Plan-scoped `*_limit` / `*_limit_reached` body codes — the machine-readable
// signal, which we trust over any natural-language wording.
const PLAN_LIMIT_CODE = /(plan|heatmap|funnel|split.?test|experiment|website|site|seat|team|usage)_?limit|limit_reached$/;

/**
 * A plan ceiling (Upgrade path), not a product bug. A decisive body code wins
 * over free-text wording: a legitimate `split_test_limit` is a plan limit even
 * if its message reads "Too many active experiments". Only when the code is
 * inconclusive do we fall back to the message (excluding rate/size wording),
 * and a bare "limit" substring never counts. Payment-required (402) is a plan
 * limit; rate-limited (429) never is.
 */
export const isPlanLimitApiError = (error: unknown): boolean => {
  if (!(error instanceof ApiError)) return false;
  // 429 is rate limiting — retry later, not upgrade.
  if (error.status === 429) return false;

  // The body code is authoritative when present — check it before the message,
  // so natural-language wording can't override a valid plan-limit code.
  const code = getErrorBodyCode(error.body)?.toLowerCase() ?? "";
  if (code) {
    if (PLAN_LIMIT_CODE.test(code)) return true;
    if (NON_PLAN_LIMIT_CODE.test(code)) return false;
  }

  if (error.status === 402) return true;

  // No decisive code — fall back to message wording, excluding rate/size limits.
  const message = error.message.toLowerCase();
  if (NON_PLAN_LIMIT_CODE.test(message)) return false;
  return message.includes("plan limit") || message.includes("limit reached");
};

/**
 * Normalize any thrown error into safe, user-facing copy. Backend 5xx failures
 * can return plain-text bodies (e.g. "Internal Server Error") that fail JSON
 * parsing with a raw SyntaxError — this ensures internal/parser error text
 * never reaches the user, while still surfacing real validation messages.
 */
export const getUserFacingApiError = (
  error: unknown,
  fallback = "Something went wrong. Please try again."
): string => {
  if (error instanceof ApiError) {
    if (error.status >= 500) {
      return "Something went wrong on our end. Your setup is saved. Please try again.";
    }
    if (isPlanLimitApiError(error)) {
      return "You've reached a limit of your current plan. Upgrade to continue.";
    }
    const genericMessage = `HTTP error! status: ${error.status}`;
    if (error.message && error.message !== genericMessage) {
      return error.message;
    }
    return fallback;
  }

  if (error instanceof TypeError && error.message.includes("Failed to fetch")) {
    return "Couldn't reach Humblytics. Check your connection and try again.";
  }

  return fallback;
};

const getTimezone = () => {
  const currentTimezone = localStorage.getItem("timezone");
  if (!currentTimezone) {
    const match = timeZones.find((timeZone) =>
      timeZone.utc.includes(Intl.DateTimeFormat().resolvedOptions().timeZone)
    );
    if (match) {
      return match.utc[0];
    }
  } else {
    const match = timeZones.find((timeZone) =>
      timeZone.utc.includes(currentTimezone)
    );
    if (match) {
      return match.utc[0];
    }
  }
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const getRequest = async (endpoint: string, token: string) => {
  const payload = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    credentials: "include" as RequestCredentials,
  };

  try {
    const response = await fetch(API_URL + endpoint, payload);
    if (!response.ok) {
      // Don't throw for 404s or 400s - let caller handle gracefully
      if (response.status === 404 || response.status === 400) {
        return null;
      }
      console.error(
        `API request failed: ${response.status} ${response.statusText} for ${endpoint}`
      );
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    // Re-throw network errors but log them with context
    if (
      error instanceof TypeError &&
      error.message.includes("Failed to fetch")
    ) {
      // Network error - likely CORS, offline, or server down
      console.warn(`Network error for ${endpoint}:`, error.message);
    } else {
      console.error(`API request error for ${endpoint}:`, error);
    }
    throw error;
  }
};

/**
 * Get sites data from humblytics.
 */
export const getAllProperties = async (token: string) => {
  const endpoint = `property/all`;
  return await getRequest(endpoint, token);
};

/**
 * Create a new property (website)
 */
export const createProperty = async (
  name: string,
  domain: string,
  token: string
) => {
  const endpoint = "property";
  const payload = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify({ name, domain }),
  };
  const response = await fetch(API_URL + endpoint, payload);
  if (!response.ok) {
    throw new Error(`Failed to create property: ${response.status}`);
  }
  return await response.json();
};

/**
 * Update an existing property (safe fallback if backend not present)
 */
export const updateProperty = async (
  propertyId: string,
  updates: { name?: string; domain?: string },
  token: string
) => {
  const endpoint = "property";
  const payload = {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify({ propertyId, ...updates }),
  };
  const response = await fetch(API_URL + endpoint, payload);
  if (!response.ok) throw new Error("Failed to update property");
  return await response.json();
};

/**
 * Pull high-level analytics stats from humblytics
 * @param granularityPeriod
 * @returns
 */
export const getAllStats = async (
  granularityPeriod: string,
  propertyId: string,
  filters: any[],
  token: string,
  includeCMS: boolean = false
) => {
  // Create an instance of URLSearchParams
  const urlParams = new URLSearchParams();
  // Iterate through the array and append each key-value pair as a separate URL parameter
  filters.forEach((filter: any) => {
    let value = filter.value;
    if (filter.category === "Location") {
      value = country_convert_reverse[removeEmojis(value)];
    }
    if (filter.category === "Device Type") {
      value =
        filter.value === "Desktop"
          ? "0"
          : filter.value === "Mobile"
          ? "1"
          : filter.value;
    }
    urlParams.append(filter.category, value);
  });
  const endpoint = `metrics/all/${propertyId}/?granularityPeriodPair=${granularityPeriod}&timezone=${getTimezone()}&${urlParams.toString()}${
    includeCMS ? "&includeCMS=true" : ""
  }`;
  return await getRequest(endpoint, token);
};

/**
 * Fetch only revenue + subscription data for a property.
 * Replaces the revenue portion of the legacy metrics/all endpoint.
 */
export const getPropertyRevenue = async (
  granularityPeriod: string,
  propertyId: string,
  token: string
) => {
  const endpoint = `metrics/${propertyId}/revenue?granularityPeriodPair=${granularityPeriod}&timezone=${getTimezone()}`;
  return await getRequest(endpoint, token);
};

/**
 * Fetch subscription data for a property (Stripe-backed, paginated server-side).
 * Kept separate from getPropertyRevenue because this endpoint can take 2–5s on
 * large accounts; callers should fire both in parallel and merge results.
 */
export const getPropertySubscriptions = async (
  propertyId: string,
  token: string
) => {
  const endpoint = `metrics/${propertyId}/subscriptions`;
  return await getRequest(endpoint, token);
};

/**
 * Pull high-level analytics stats from humblytics
 * @param granularityPeriod
 * @returns
 */
const dashBoardStats = async (
  granularityPeriod: string,
  propertyId: string,
  token: string
) => {
  const endpoint = `metrics/all/${propertyId}?granularityPeriodPair=${granularityPeriod}&timezone=${getTimezone()}`;
  return await getRequest(endpoint, token);
};

/**
 * Pull detailed referer data from humblytics
 * @param granularityPeriod
 * @returns
 */
const getRefererData = async (
  refererGroup: string,
  granularityPeriod: string,
  propertyId: string,
  token: string
) => {
  const endpoint = `metrics/${propertyId}/referer_group/?referer_group=${refererGroup}&granularityPeriodPair=${granularityPeriod}&timezone=${getTimezone()}`;
  return await getRequest(endpoint, token);
};

/**
 * Get real time user analytics from humblytics
 * @param granularityPeriod
 * @returns
 */
export const realTimeUserStats = async (propertyId: string, token: string) => {
  const endpoint = `metrics/${propertyId}/realtime`;
  return await getRequest(endpoint, token);
};

/**
 * Pull detailed real-time events with full session context including country
 * @param propertyId
 * @param token
 * @param filters Optional filters for page, device, location, etc.
 * @returns Combined sessions and metrics with country data
 */
export const getRealtimeEvents = async (
  propertyId: string,
  token: string,
  filters?: Record<string, string | null>
) => {
  const params = new URLSearchParams();
  params.append("timezone", getTimezone());

  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });
  }

  const endpoint = `metrics/${propertyId}/realtime-events?${params.toString()}`;
  return await getRequest(endpoint, token);
};

/**
 * Pull in-depth page data from humblytics
 * @param granularityPeriod
 * @returns promise with data
 */
export const getPageStats = async (
  granularityPeriod: string,
  propertyId: string,
  token: string
) => {
  const endpoint = `metrics/${propertyId}/pages?granularityPeriodPair=${granularityPeriod}&timezone=${getTimezone()}`;
  return await getRequest(endpoint, token);
};

/**
 * Pull in-depth form data from humblytics
 * @param granularityPeriod
 * @returns
 */
export const getFormStats = async (
  granularityPeriod: string,
  propertyId: string,
  filters: any[],
  token: string
) => {
  // Create an instance of URLSearchParams
  const urlParams = new URLSearchParams();
  // Iterate through the array and append each key-value pair as a separate URL parameter
  filters.forEach((filter: any) => {
    let value = filter.value;
    if (filter.category === "Location") {
      value = country_convert_reverse[removeEmojis(value)];
    }
    if (filter.category === "Device Type") {
      value =
        filter.value === "Desktop"
          ? "0"
          : filter.value === "Mobile"
          ? "1"
          : filter.value;
    }
    urlParams.append(filter.category, value);
  });
  const endpoint = `metrics/${propertyId}/forms/?granularityPeriodPair=${granularityPeriod}&timezone=${getTimezone()}&${urlParams.toString()}`;
  return await getRequest(endpoint, token);
};

/**
 * Get detailed click data
 * @param granularityPeriod
 * @returns
 */
export const getClickStats = async (
  granularityPeriod: string,
  propertyId: string,
  filters: any[],
  token: string
) => {
  // Create an instance of URLSearchParams
  const urlParams = new URLSearchParams();
  // Iterate through the array and append each key-value pair as a separate URL parameter
  filters.forEach((filter: any) => {
    let value = filter.value;
    if (filter.category === "Location") {
      value = country_convert_reverse[removeEmojis(value)];
    }
    if (filter.category === "Device Type") {
      value =
        filter.value === "Desktop"
          ? "0"
          : filter.value === "Mobile"
          ? "1"
          : filter.value;
    }
    urlParams.append(filter.category, value);
  });
  const endpoint = `metrics/${propertyId}/buttons/?granularityPeriodPair=${granularityPeriod}&timezone=${getTimezone()}&${urlParams.toString()}`;
  return await getRequest(endpoint, token);
};

/**
 * Get experiments list with metrics
 */
export const getExperimentsList = async (propertyId: string, token: string) => {
  // Use demo data interception for demo site
  return interceptDemoAPI(
    propertyId,
    async () => {
      // Use /details endpoint to get metrics along with experiments
      const endpoint = `experiments/${propertyId}/details`;
      const rawData = await getRequest(endpoint, token);

      // Transform each experiment's metrics
      if (Array.isArray(rawData)) {
        return rawData.map((exp) => transformExperimentMetrics(exp));
      }

      return rawData;
    },
    demoGenerators.experiments
  );
};

/**
 * Transform raw experiment metrics to frontend format
 */
const transformExperimentMetrics = (rawData: any) => {
  if (!rawData || !rawData.experiment) return rawData;

  const {
    experiment: rawExperiment,
    urlAMetrics,
    urlBMetrics,
    urlCMetrics,
    urlDMetrics,
    ...rest
  } = rawData;
  const experiment = {
    ...rawExperiment,
    testGoal: normalizeTestGoal(rawExperiment.testGoal),
  };
  // Normalize variants: assign fallback id/label/isControl for nocode experiments that lack them
  // Use same scheme as useExperiments list hook: variant-${index}, "Control", "Variant A/B/C..."
  const isComponentTest = rawData.experimentType?.startsWith('component_');
  const variants = (experiment.variants || []).map((v: any, index: number) => ({
    ...v,
    id: v.id || `variant-${index}`,
    isControl: v.isControl ?? (index === 0),
    label: v.label || (index === 0
      ? 'Control'
      : `Variant ${String.fromCharCode(isComponentTest ? 64 + index : 65 + index)}`),
  }));

  // Helper to extract stats from metrics
  const extractVariantStats = (metrics: any, testGoal?: string) => {
    if (!metrics)
      return {
        sessions: 0,
        conversions: 0,
        conversionRate: 0,
        bounceRate: null,
        avgTimeOnPage: 0,
      };

    // Extract sessions
    const sessions = metrics.sessions?.[0]
      ? parseInt(String(metrics.sessions[0].count || "0"))
      : 0;

    // Extract bounces
    const bounces = metrics.bounces?.[0]
      ? parseInt(String(metrics.bounces[0].count || "0"))
      : 0;

    // Extract views and time on page
    const views = metrics.views?.[0] || {};
    const totalSessionLength = parseInt(
      String(views.total_session_length || "0")
    );
    const scrollPercent = parseFloat(String(views.scroll_percent || "0"));

    // Form submits from the top targets, for every goal: results show them next
    // to the goal. Named targets only when the form has one (each named submit
    // also arrives as a generic "Form" event).
    const formTopEvents: any[] = (metrics.topEvents || []).filter(
      (evt: any) => evt.action === "form_submission" || evt.action === "form_submit"
    );
    const isGenericFormTarget = (t: unknown) => /^(form|submit)$/i.test(String(t ?? "").trim());
    const namedFormEvents = formTopEvents.filter((evt) => !isGenericFormTarget(evt.target));
    const formSubmissions = (namedFormEvents.length > 0 ? namedFormEvents : formTopEvents).reduce(
      (sum: number, evt: any) => sum + parseInt(String(evt.event_count || "0")),
      0
    );

    // Calculate conversions based on test goal
    let conversions = 0;
    let goalEventScale = 1;
    switch (testGoal) {
      case "page_funnel":
      case "external_page_funnel":
        conversions =
          metrics.funnelConversions?.reduce(
            (sum: number, conv: any) => sum + parseInt(conv.count || "0"),
            0
          ) || 0;
        break;
      case "revenue":
        conversions =
          metrics.revenueConversions?.reduce(
            (sum: number, conv: any) => sum + parseInt(conv.count || "0"),
            0
          ) || 0;
        break;
      case "form_submissions":
      case "Form Submission": {
        // The tracking script sends each submit twice when the form has an id:
        // once as a generic "Form" event and once under its own target
        // (product-demo-form-submit). Summing totalEvents counted one visitor's
        // submit as two. When a named form target exists, count named targets
        // only; a page whose forms are all generic keeps the generic count.
        const formEvents: any[] = (metrics.topEvents || []).filter(
          (evt: any) => evt.action === "form_submission" || evt.action === "form_submit"
        );
        const isGenericForm = (t: unknown) => /^(form|submit)$/i.test(String(t ?? "").trim());
        const named = formEvents.filter((evt) => !isGenericForm(evt.target));
        const allSubmits =
          metrics.totalEvents?.reduce(
            (sum: number, evt: any) => sum + parseInt(evt.total_count || "0"),
            0
          ) || 0;
        conversions = named.length > 0
          ? named.reduce((sum: number, evt: any) => sum + parseInt(evt.event_count || "0"), 0)
          : allSubmits;
        // The daily series comes from the same raw events; scale it by the same
        // ratio so the chart ends where the table does.
        if (named.length > 0 && allSubmits > 0) goalEventScale = Math.min(1, conversions / allSubmits);
        break;
      }
      case "click_through_rate":
      case "Click Through Rate":
      case "Increase Conversion Rate":
      default:
        // For click-through rate, count click events
        conversions =
          metrics.events
            ?.filter((evt: any) => evt.action === "click")
            .reduce(
              (sum: number, evt: any) => sum + parseInt(evt.count || "0"),
              0
            ) || 0;
        // If no click events, use totalEvents
        if (conversions === 0) {
          conversions =
            metrics.totalEvents?.reduce(
              (sum: number, evt: any) => sum + parseInt(evt.total_count || "0"),
              0
            ) || 0;
        }
        break;
    }

    // Store conversion rate as percentage (0-100) not decimal
    const conversionRate = sessions > 0 ? (conversions / sessions) * 100 : 0;
    // The experiments endpoint does not send `bounces` today. Null keeps the
    // guardrail row hidden instead of reading "0.0% vs 0.0%, no harm so far".
    const bounceRate = Array.isArray(metrics.bounces) && sessions > 0 ? (bounces / sessions) * 100 : null;
    // total_session_length is milliseconds (115,002,000 / 172 visitors read as
    // "1916m" before); avgTimeOnPage is seconds (see MetricsData).
    const avgTimeOnPage = sessions > 0 ? totalSessionLength / sessions / 1000 : 0;

    // Stripe outcomes (payments, revenue, trial starts) whenever the API sends
    // them, not only for revenue-goal tests: results show every step of the
    // outcome ladder next to the goal.
    const revenueStats: Record<string, number> = {};
    if (Array.isArray(metrics.revenueConversions)) {
      let totalRevenue = 0;
      let payments = 0;
      metrics.revenueConversions.forEach((conv: any) => {
        totalRevenue += parseFloat(String(conv.total_revenue || conv.revenue || conv.amount || '0')) / 100;
        payments += parseInt(String(conv.count || '0'));
      });
      revenueStats.totalRevenue = totalRevenue;
      revenueStats.paymentConversions = payments;
      revenueStats.averageOrderValue = payments > 0 ? totalRevenue / payments : 0;
      revenueStats.revenuePerVisitor = sessions > 0 ? totalRevenue / sessions : 0;
    }
    if (Array.isArray(metrics.freeTrialConversions)) {
      const trialConversions = metrics.freeTrialConversions.reduce(
        (sum: number, conv: any) => sum + parseInt(conv.count || '0'),
        0
      );
      revenueStats.trialConversions = trialConversions;
      revenueStats.trialConversionRate = sessions > 0 ? (trialConversions / sessions) * 100 : 0;
    }

    // scroll_percent is the average of how far down each view scrolled (0-100).
    const scrollDepth = views.scroll_percent != null && sessions > 0 ? scrollPercent : null;

    // Total Views: the views query counts every page view row.
    const totalViews = Array.isArray(metrics.views)
      ? metrics.views.reduce((sum: number, row: any) => sum + parseInt(String(row.count || "0")), 0)
      : null;
    // Every click on the page. totalEvents counts clicks unless the launch goal
    // is a form (then it counts submits), so it is null for form tests.
    const isFormGoal = testGoal === "form_submissions" || testGoal === "Form Submission";
    const totalClicks = !isFormGoal && Array.isArray(metrics.totalEvents)
      ? metrics.totalEvents.reduce((sum: number, evt: any) => sum + parseInt(String(evt.total_count || "0")), 0)
      : null;

    return {
      sessions,
      conversions,
      conversionRate,
      bounceRate,
      avgTimeOnPage,
      scrollDepth,
      formSubmissions,
      goalEventScale,
      totalViews,
      totalClicks,
      ...revenueStats,
    };
  };

  // Map metrics to variants
  const metricsMap: Record<string, any> = {
    A: urlAMetrics,
    B: urlBMetrics,
    C: urlCMetrics,
    D: urlDMetrics,
  };

  const variantsWithStats: Record<string, any> = {};

  variants.forEach((variant: any, index: number) => {
    const variantLetter = String.fromCharCode(65 + index); // 'A', 'B', 'C', 'D'
    const metrics = metricsMap[variantLetter];
    const stats = extractVariantStats(metrics, experiment.testGoal);

    variantsWithStats[variant.id] = {
      ...stats,
    };
  });

  // Build merged time series from per-variant timeSeriesSessions / timeSeriesConversions
  const buildTimeSeries = () => {
    const hasData = variants.some((_: any, index: number) => {
      const letter = String.fromCharCode(65 + index);
      return metricsMap[letter]?.timeSeriesSessions?.length > 0;
    });
    if (!hasData) return null;

    // Date range: experiment start → end (or today)
    const start = new Date(rawData.createdAt);
    start.setHours(0, 0, 0, 0);
    const end = rawData.endedAt ? new Date(rawData.endedAt) : new Date();
    end.setHours(0, 0, 0, 0);

    // Generate every day in the range
    const allDates: string[] = [];
    const cursor = new Date(start);
    while (cursor <= end) {
      allDates.push(cursor.toISOString().slice(0, 10));
      cursor.setDate(cursor.getDate() + 1);
    }

    // Build a date→{sessions,conversions} map per variant
    const variantDateMaps: Record<string, Record<string, { sessions: number; conversions: number }>> = {};
    variants.forEach((variant: any, index: number) => {
      const letter = String.fromCharCode(65 + index);
      const metrics = metricsMap[letter];
      const dateMap: Record<string, { sessions: number; conversions: number }> = {};

      (metrics?.timeSeriesSessions || []).forEach((row: any) => {
        const key = new Date(row.date).toISOString().slice(0, 10);
        if (!dateMap[key]) dateMap[key] = { sessions: 0, conversions: 0 };
        dateMap[key].sessions += parseInt(String(row.sessions || '0'));
      });

      (metrics?.timeSeriesConversions || []).forEach((row: any) => {
        const key = new Date(row.date).toISOString().slice(0, 10);
        if (!dateMap[key]) dateMap[key] = { sessions: 0, conversions: 0 };
        dateMap[key].conversions += parseInt(String(row.conversions || '0'));
      });

      variantDateMaps[variant.id] = dateMap;
    });

    // Merge into one row per date with gap-fill zeros
    return allDates.map((dateKey) => {
      const point: Record<string, any> = { date: dateKey };
      variants.forEach((variant: any) => {
        const day = variantDateMaps[variant.id]?.[dateKey];
        const sessions = day?.sessions ?? 0;
        const conversions = (day?.conversions ?? 0) * (variantsWithStats[variant.id]?.goalEventScale ?? 1);
        point[`${variant.id}_conversionRate`] = sessions > 0 ? (conversions / sessions) * 100 : 0;
        point[`${variant.id}_sessions`] = sessions;
      });
      return point;
    });
  };

  return {
    ...rest,
    experiment,
    variants: variantsWithStats,
    timeSeries: buildTimeSeries(),
    // Keep raw metrics for backwards compatibility
    urlAMetrics,
    urlBMetrics,
    urlCMetrics,
    urlDMetrics,
  };
};

/**
 * Get experiment metrics by ID
 */
export const getExperimentMetricsById = async (
  propertyId: string,
  experimentId: string,
  token: string,
  includeTimeSeries: boolean = false
) => {
  const endpoint = includeTimeSeries
    ? `experiments/${propertyId}/${experimentId}?includeTimeSeries=true`
    : `experiments/${propertyId}/${experimentId}`;
  const rawData = await getRequest(endpoint, token);
  return transformExperimentMetrics(rawData);
};

export type ExperimentGoalMetricsResponse =
  | { status: "ok"; byLetter: Record<string, GoalMetrics | null> }
  | { status: "invalid_destination" };

/**
 * Opt-in per-goal metrics for the results page's "Measure by" toggle:
 * `?goals=all[&destination=<path or URL>]`. A separate, secondary request: the
 * backend answers 500 when one of the extra queries fails, so the launch-goal
 * view never depends on it. Backends without the feature ignore the params
 * and return no `goalMetrics` (every letter null). A rejected destination is a
 * 400, which `getRequest` turns into null.
 * Contract: docs/TEMP_EXPERIMENT_GOAL_METRICS_CONTRACT.md.
 */
export const getExperimentGoalMetrics = async (
  propertyId: string,
  experimentId: string,
  token: string,
  destination?: string | null,
  /** JSON `{ preset, targets }`: live preview of a click set (goalMetrics v2). */
  clickSet?: string | null
): Promise<ExperimentGoalMetricsResponse> => {
  const params = new URLSearchParams({ goals: "all" });
  if (destination) params.set("destination", destination);
  if (clickSet) params.set("clickSet", clickSet);
  const rawData = await getRequest(`experiments/${propertyId}/${experimentId}?${params.toString()}`, token);
  if (rawData == null) return { status: "invalid_destination" };
  const byLetter: Record<string, GoalMetrics | null> = {};
  for (const letter of ["A", "B", "C", "D"]) {
    byLetter[letter] = parseGoalMetrics(rawData[`url${letter}Metrics`]?.goalMetrics);
  }
  return { status: "ok", byLetter };
};

/**
 * Get experiment traffic list
 */
const getExperimentTrafficList = async (
  propertyId: string,
  token: string
) => {
  const endpoint = `experiments/traffic/${propertyId}`;
  return await getRequest(endpoint, token);
};

/**
 * Get plan usage vs. limits for every limit-bearing feature on a property
 * (funnels, heatmaps, split tests, team seats, websites, revenue attribution).
 * Limits are always resolved against the property's subscription owner, not
 * the requesting user — safe to call for read-only magic property links too.
 */
export const getSubscriptionUsage = async (
  propertyId: string,
  token: string
): Promise<import("@/lib/types/billing").PlanUsageResponse | null> => {
  const endpoint = `subscription/${propertyId}/usage`;
  return await getRequest(endpoint, token);
};

/**
 * Get account experiment usage
 */
const getAccountExperimentUsage = async (token: string) => {
  const endpoint = "account/experiment-usage";
  return await getRequest(endpoint, token);
};

/**
 * Get AI chat usage (AI Insights) for the authenticated user
 */
export const getAIChatUsage = async (token: string) => {
  const endpoint = "ai/chat/usage";
  return await getRequest(endpoint, token);
};

/**
 * Create an experiment
 */
export const createExperiment = async (
  propertyId: string,
  variants: Array<{
    id: string;
    url: string;
    notes: string;
    label: string;
    isControl: boolean;
    screenshots?: string[];
    instructions?: ComponentInstruction[]; // For component tests
  }>,
  experimentName: string,
  isUserLevel: boolean,
  token: string,
  testGoal?: string,
  geoRestriction?: GeoRestriction,
  audienceRestriction?: AudienceRestriction,
  finishPageUrl?: string,
  testType?: string,
  notes?: string,
  trafficDistribution?: number[],
  draftId?: string,
  autoStopDurationDays?: number | null
) => {
  const endpoint = `experiments/${propertyId}`;
  const payload = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify({
      propertyId,
      variants,
      experimentName,
      isUserLevel,
      testGoal,
      geoRestriction,
      audienceRestriction,
      finishPageUrl,
      testType,
      notes,
      trafficDistribution,
      draftId,
      autoStopDurationDays: autoStopDurationDays ?? null,
    }),
  };

  const response = await fetch(API_URL + endpoint, payload);
  if (!response.ok) {
    throw await createApiError(response);
  }
  const data = await parseResponseBody(response);
  if (data && typeof data === "object" && (data as Record<string, unknown>).success === false) {
    throw new ApiError(response.status, data, getApiErrorMessage(response.status, data));
  }
  return data;
};

/**
 * Stop/pause an experiment
 */
export const stopExperiment = async (
  propertyId: string,
  experimentId: string,
  token: string
) => {
  const endpoint = `experiments/${propertyId}`;
  const payload = {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify({ experimentId }),
  };
  const response = await fetch(API_URL + endpoint, payload);
  if (!response.ok) {
    throw await createApiError(response);
  }
  return await parseResponseBody(response);
};

/**
 * Save the goal a test's results are read on (`null` = back to the launch
 * goal). `testGoal` stays the launch goal; the server keeps the history.
 * Errors carry the status (ApiError): 400 validation, 401 / 403 auth, 404
 * experiment not on this property (also an old backend: detect support from
 * goalMetrics v2, not from this), 409 concurrent change, 500.
 */
export const putExperimentResultsGoal = async (
  propertyId: string,
  experimentId: string,
  resultsGoal: ResultsGoal | null,
  token: string
): Promise<{ resultsGoal: unknown; resultsGoalHistory: unknown }> => {
  const response = await fetch(API_URL + `experiments/${propertyId}/${experimentId}/results-goal`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify({ resultsGoal }),
  });
  if (!response.ok) throw await createApiError(response);
  const body = (await parseResponseBody(response)) as Record<string, unknown> | null;
  return { resultsGoal: body?.resultsGoal ?? null, resultsGoalHistory: body?.resultsGoalHistory ?? [] };
};

export const trackSplitTestSetup = (
  propertyId: string,
  step: string,
  action: "viewed" | "completed" | "abandoned",
  token: string
): void => {
  // Fire-and-forget; ignore errors to never block the UI
  fetch(API_URL + `experiments/${propertyId}/track-setup`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
    body: JSON.stringify({ step, action }),
  }).catch(() => {});
};

/**
 * Resume a paused experiment
 */
export const resumeExperiment = async (
  propertyId: string,
  experimentId: string,
  token: string
) => {
  const endpoint = `experiments/${propertyId}`;
  const payload = {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify({ experimentId, status: "active" }),
  };
  const response = await fetch(API_URL + endpoint, payload);
  // Keep the status (404 = not on this property) for the caller's toast.
  if (!response.ok) throw await createApiError(response);
  return await parseResponseBody(response);
};

/**
 * Archive an experiment
 */
export const archiveExperiment = async (
  propertyId: string,
  experimentId: string,
  token: string
) => {
  const endpoint = `experiments/${propertyId}/archive`;
  const payload = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify({ experimentId }),
  };
  const response = await fetch(API_URL + endpoint, payload);
  if (!response.ok) {
    throw await createApiError(response);
  }
  return await parseResponseBody(response);
};

export type ExperimentDecisionPayload = {
  experimentId: string
  /** Variant id declared the winner (or null if no-ship / inconclusive). */
  winnerVariantId: string | null
  /** ship = roll winner to 100%; stop = call without shipping; no_ship = keep control. */
  action: "ship" | "stop" | "no_ship"
  rationale?: string
  liftPercent?: number | null
  significancePercent?: number | null
}

/**
 * Record an experiment decision (call the test) and optionally promote the
 * winning variant to 100% traffic for no-code element tests.
 * Backend: POST experiments/{propertyId}/decision
 * Falls back gracefully when the endpoint is not yet deployed (404/501).
 */
export const recordExperimentDecision = async (
  propertyId: string,
  decision: ExperimentDecisionPayload,
  token: string
) => {
  const endpoint = `experiments/${propertyId}/decision`
  const response = await fetch(API_URL + endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify(decision),
  })
  if (response.status === 404 || response.status === 501) {
    // Endpoint not deployed yet — caller persists locally and stops the test.
    return { ok: false, pendingBackend: true as const }
  }
  if (!response.ok) throw new Error("Failed to record experiment decision")
  return { ok: true as const, ...(await response.json()) }
}

/**
 * Promote a winning variant to 100% (element / component tests).
 * Backend: POST experiments/{propertyId}/promote
 */
export const promoteExperimentVariant = async (
  propertyId: string,
  experimentId: string,
  winnerVariantId: string,
  token: string
) => {
  const endpoint = `experiments/${propertyId}/promote`
  const response = await fetch(API_URL + endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify({ experimentId, winnerVariantId }),
  })
  if (response.status === 404 || response.status === 501) {
    return { ok: false, pendingBackend: true as const }
  }
  if (!response.ok) throw new Error("Failed to promote winning variant")
  return { ok: true as const, ...(await response.json()) }
}

/**
 * Analyze experiment with AI
 */
const analyzeExperiment = async (
  experimentData: any,
  token: string,
  propertyId: string
) => {
  const endpoint = `experiment-analysis/${propertyId}`;
  const payload = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify({ experimentData }),
  };
  const response = await fetch(API_URL + endpoint, payload);
  if (!response.ok) throw new Error("Failed to analyze experiment");
  return await response.json();
};

/**
 * Get pages available for experiments
 */
export const getExperimentPages = async (token: string, propertyId: string) => {
  const endpoint = `experiments/${propertyId}/pages`;
  return await getRequest(endpoint, token);
};

/**
 * Validate experiment setup
 */
const validateExperimentSetup = async (
  propertyId: string,
  token: string
) => {
  const endpoint = `validate-experiment-setup`;
  const payload = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify({ propertyId }),
  };
  const response = await fetch(API_URL + endpoint, payload);
  if (!response.ok) throw new Error("Failed to validate experiment setup");
  return await response.json();
};

/**
 * Update user profile (e.g., name)
 */
const updateProfile = async (
  updates: { name?: string },
  token: string
) => {
  const endpoint = "auth/update-profile";
  const payload = {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify(updates),
  };
  const response = await fetch(API_URL + endpoint, payload);
  if (!response.ok) throw new Error("Failed to update profile");
  return await response.json();
};

/**
 * Change user password
 */
const changePassword = async (
  currentPassword: string,
  newPassword: string,
  token: string
) => {
  const endpoint = "auth/change-password";
  const payload = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify({ currentPassword, newPassword }),
  };
  const response = await fetch(API_URL + endpoint, payload);
  if (!response.ok) throw new Error("Failed to change password");
  return await response.json();
};

/**
 * Site settings: IP filters
 */
export const updateIPFilters = async (
  propertyId: string,
  ipAddresses: string[],
  token: string
) => {
  const endpoint = "update-ignore-ip";
  const payload = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify({ propertyId, ipAddresses }),
  };
  const response = await fetch(API_URL + endpoint, payload);
  if (!response.ok) throw new Error("Failed to update IP filters");
  const text = await response.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return {};
  }
};

/**
 * Site settings: custom properties
 */
const updateCustomProperties = async (
  propertyId: string,
  keys: string[],
  token: string
) => {
  const endpoint = "property/custom-properties";
  const payload = {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify({ propertyId, keys }),
  };
  const response = await fetch(API_URL + endpoint, payload);
  if (!response.ok) throw new Error("Failed to update custom properties");
  return await response.json();
};

/**
 * Site settings: revenue settings
 */
const updateRevenueSettings = async (
  propertyId: string,
  settings: { eventName: string },
  token: string
) => {
  const endpoint = "property/revenue-settings";
  const payload = {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify({ propertyId, ...settings }),
  };
  const response = await fetch(API_URL + endpoint, payload);
  if (!response.ok) throw new Error("Failed to update revenue settings");
  return await response.json();
};

/**
 * Get heatmap data
 */
export const getHeatmapAuth = async (propertyId: string, token: string) => {
  const endpoint = `heatmap/auth/${propertyId}`;
  return await getRequest(endpoint, token);
};

/**
 * Get heatmap pages
 */
export const getHeatmapPages = async (propertyId: string, token: string) => {
  const endpoint = `heatmap-pages/${propertyId}`;
  return await getRequest(endpoint, token);
};

/**
 * Add a new heatmap page
 */
export const addHeatmapPage = async (
  propertyId: string,
  pageUrl: string,
  title: string | null,
  token: string
) => {
  const endpoint = `heatmap-pages/${propertyId}`;
  const payload = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ pageUrl, title }),
    credentials: "include" as RequestCredentials,
  };

  try {
    const response = await fetch(API_URL + endpoint, payload);
    if (!response.ok) {
      throw await createApiError(response);
    }
    return await response.json();
  } catch (error) {
    console.error("API request error:", error);
    throw error;
  }
};

/**
 * Remove a heatmap page
 */
export const removeHeatmapPage = async (
  propertyId: string,
  pageUrl: string,
  token: string
) => {
  const endpoint = `heatmap-pages/${propertyId}`;
  const payload = {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ pageUrl }),
    credentials: "include" as RequestCredentials,
  };

  try {
    const response = await fetch(API_URL + endpoint, payload);
    if (!response.ok) {
      throw await createApiError(response);
    }
    return await response.json();
  } catch (error) {
    console.error("API request error:", error);
    throw error;
  }
};

/**
 * Capture screenshot for a heatmap page
 */
export const captureHeatmapScreenshot = async (
  propertyId: string,
  pageUrl: string,
  deviceType: "mobile" | "desktop" | null,
  token: string
) => {
  const endpoint = `heatmap-pages/${propertyId}/screenshot`;
  const payload = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ pageUrl, deviceType }),
    credentials: "include" as RequestCredentials,
  };

  try {
    const response = await fetch(API_URL + endpoint, payload);
    if (!response.ok) {
      throw await createApiError(response);
    }
    return await response.json();
  } catch (error) {
    console.error("API request error:", error);
    throw error;
  }
};

/**
 * Get heatmap data for a specific page
 */
export const getHeatmapData = async (
  granularityPeriodPair: string,
  pageUrl: string,
  token: string,
  timezone: string = "America/Detroit",
  propertyId: string
) => {
  // Extract pathname from pageUrl for filtering
  const pagePath = pageUrl.startsWith("http")
    ? new URL(pageUrl).pathname
    : pageUrl;

  const tz = timezone || getTimezone();
  const endpoint = `metrics/${propertyId}/heatmap/?granularityPeriodPair=${granularityPeriodPair}&timezone=${tz}&page=${encodeURIComponent(
    pagePath
  )}&include_scroll_depth=true`;
  return await getRequest(endpoint, token);
};

/**
 * Get all pages for a property (used in add page dialog)
 */
export const getPages = async (token: string, propertyId: string) => {
  const endpoint = `experiments/${propertyId}/pages`;
  return await getRequest(endpoint, token);
};

/**
 * Get saved funnels
 */
export const getSavedFunnels = async (propertyId: string, token: string) => {
  const endpoint = `metrics/${propertyId}/funnels`;
  return await getRequest(endpoint, token);
};

/**
 * Save a funnel
 */
export const saveFunnel = async (
  funnel: any,
  propertyId: string,
  token: string
) => {
  const endpoint = `metrics/${propertyId}/funnels`;
  const payload = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify(funnel),
  };

  const response = await fetch(API_URL + endpoint, payload);
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    if (response.status === 402) {
      const err: any = new Error('Funnel limit reached');
      err.status = 402;
      err.plan = body.plan;
      err.limit = body.limit;
      err.current = body.current;
      throw err;
    }
    throw new Error(body.message || 'Failed to save funnel');
  }
  return await response.json();
};


export const updateFunnel = async (
  funnel: any,
  propertyId: string,
  funnelId: string,
  token: string
) => {
  const endpoint = `metrics/${propertyId}/funnels/${funnelId}`;
  const payload = {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify(funnel),
  };

  const response = await fetch(API_URL + endpoint, payload);
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    if (response.status === 404 || response.status === 405) {
      const err: any = new Error("Couldn't save changes. Please try again later");
      err.status = response.status;
      throw err;
    }
    throw new Error(body.message || "Failed to update funnel");
  }
  return await response.json();
};

/**
 * Delete a funnel
 */
export const deleteFunnel = async (
  propertyId: string,
  funnelId: string,
  token: string
) => {
  const endpoint = `metrics/${propertyId}/funnels/${funnelId}`;
  const payload = {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
  };
  const response = await fetch(API_URL + endpoint, payload);
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || "Failed to delete funnel");
  }
  const responseText = await response.text();
  return responseText ? JSON.parse(responseText) : null;
};

/**
 * Get export data
 */
const getExportData = async (
  granularityPeriod: string,
  propertyId: string,
  filters: any[],
  dataTypes: string[],
  token: string
) => {
  // Create an instance of URLSearchParams
  const urlParams = new URLSearchParams();
  // Iterate through the array and append each key-value pair as a separate URL parameter
  filters.forEach((filter: any) => {
    let value = filter.value;
    if (filter.category === "Location") {
      value = country_convert_reverse[removeEmojis(value)];
    }
    if (filter.category === "Device Type") {
      value =
        filter.value === "Desktop"
          ? "0"
          : filter.value === "Mobile"
          ? "1"
          : filter.value;
    }
    urlParams.append(filter.category, value);
  });
  urlParams.append("dataTypes", dataTypes.join(","));
  const endpoint = `metrics/${propertyId}/export/?granularityPeriodPair=${granularityPeriod}&timezone=${getTimezone()}&${urlParams.toString()}`;
  return await getRequest(endpoint, token);
};

/**
 * Analyze site with AI for optimization suggestions
 */
export const analyzeSite = async (
  url: string,
  domain: string,
  propertyId: string,
  token: string
) => {
  const endpoint = `analyze/${propertyId}`;
  const payload = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ url, domain }),
  };

  const response = await fetch(API_URL + endpoint, payload);
  if (!response.ok) throw new Error("Failed to analyze site");
  return await response.json();
};

/**
 * Get site analyses
 */
export const getAnalyses = async (propertyId: string, token: string) => {
  const endpoint = `analyses/${propertyId}`;
  return await getRequest(endpoint, token);
};

/**
 * Verify magic link for authentication
 */
export const verifyMagicLink = async (
  token: string,
  password: string,
  newUser: boolean
) => {
  const fingerprint = JSON.stringify([
    navigator.hardwareConcurrency,
    (navigator as any).deviceMemory,
    navigator.maxTouchPoints,
  ]);
  const data = {
    t: token,
    p: password,
    fingerprint,
  };
  const endpoint = newUser
    ? "auth/magic-register-link"
    : "auth/magic-sign-in-link";
  const payload = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  };
  const response = await fetch(API_URL + endpoint, payload);
  if (response.ok) {
    const { authToken } = await response.json();
    return authToken;
  }
  return false;
};

/**
 * Verify new email change token
 */
export const verifyNewEmail = async (
  changeEmailToken: string,
  token: string
) => {
  const endpoint = "user/email";
  const data = {
    changeEmailToken,
  };
  const payload = {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
    credentials: "include" as RequestCredentials,
    body: JSON.stringify(data),
  };
  const response = await fetch(API_URL + endpoint, payload);
  return await response.json();
};

/**
 * Update user name
 */
export const updateName = async (name: string, token: string) => {
  const endpoint = "user/name";
  const data = {
    name,
  };
  const payload = {
    method: "POST",
    credentials: "include" as RequestCredentials,
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify(data),
  };
  const response = await fetch(API_URL + endpoint, payload);
  return await response.json();
};

/**
 * Update user email (triggers verification)
 */
export const updateEmail = async (email: string, token: string) => {
  const endpoint = "user/email";
  const data = {
    email,
  };
  const payload = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
    credentials: "include" as RequestCredentials,
    body: JSON.stringify(data),
  };
  const response = await fetch(API_URL + endpoint, payload);
  return await response.json();
};

/**
 * Get Webflow integration status
 */
const getWebflowIntegration = async (token: string) => {
  const endpoint = "webflow-integration";
  const payload = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
  };
  const response = await fetch(API_URL + endpoint, payload);
  return await response.json();
};

/**
 * Remove Webflow integration
 */
const removeWebflowIntegration = async (token: string) => {
  const endpoint = "webflow-integration";
  const payload = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
  };
  const response = await fetch(API_URL + endpoint, payload);
  return await response.json();
};

// ============================================================================
// Workflow API Functions
// ============================================================================

export interface WorkflowAuditResult {
  timestamp: string
  domain: string
  analysis_type: string
  total_pages?: number
  successful_pages?: number
  page_results?: Array<{
    page_url: string
    page_type: string
    specialist_analyses: Array<{
      specialist: string
      analysis: string
      opportunities: string
      priority_score: number
      tests: Array<{
        title: string
        category: string
        hypothesis: string
        control: string
        variant: string
        expectedImpact: string
        confidence: string
        implementation_complexity: string
        priority_score: number
      }>
    }>
  }>
  aggregated_insights?: {
    overall_assessment: string
    optimization_score: number
    key_themes: string[]
    priority_recommendations: Array<{
      title: string
      description: string
      impact_areas: string[]
      priority_score: number
    }>
    quick_wins: string[]
    strategic_initiatives: string[]
  }
  cross_page_insights?: {
    consistency_score: number
    user_flow_assessment: string
    key_inconsistencies: string[]
    cross_page_opportunities: Array<{
      title: string
      description: string
      affected_pages: string[]
      impact_potential: string
      priority_score: number
    }>
    conversion_funnel_issues: string[]
    recommendations: string[]
  }
  summary?: {
    totalTests: number
    specialists: string[]
    overallScore: number
    pages_analyzed?: number
  }
}

/**
 * Poll GET /analyses/:propertyId until the given analysisId is completed or errors.
 * Resolves with the result payload or throws on error/timeout.
 */
const pollAnalysis = async (
  propertyId: string,
  analysisId: string,
  token: string,
  timeoutMs = 180000,
  intervalMs = 4000
): Promise<any> => {
  const deadline = Date.now() + timeoutMs;

  while (Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, intervalMs));

    const resp = await fetch(`${API_URL}analyses/${propertyId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!resp.ok) throw new Error("Failed to poll analysis status");

    const { analyses } = await resp.json();
    const analysis = (analyses as any[]).find((a) => a.id === analysisId);

    if (!analysis) continue;
    if (analysis.status === "completed") return analysis.result;
    if (analysis.status === "error")
      throw new Error(analysis.errorMessage || "Analysis failed");
  }

  throw new Error("Analysis timed out. Please try again.");
};

/**
 * Run a multi-page site analysis for landing page audit
 * Uses the backend's AI-powered analysis with multiple specialist agents
 */
const runLandingPageAudit = async (
  propertyId: string,
  domain: string,
  pageUrl: string,
  token: string
): Promise<WorkflowAuditResult> => {
  const endpoint = `analyze/${propertyId}`;
  const payload = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ url: pageUrl, domain }),
  };

  const response = await fetch(API_URL + endpoint, payload);
  if (!response.ok) throw new Error("Failed to run landing page audit");
  const { analysisId } = await response.json();
  return await pollAnalysis(propertyId, analysisId, token);
};

/**
 * Legacy weekly summary for growth-report notifications.
 * Prefer `getTrafficSummary('LAST_7_DAYS', ...)` from external-api for
 * dashboard "last 7 days" views — this endpoint's shape/window does not
 * match rolling 7d totals shown in analytics.
 */
const getWeeklyDataSummary = async (
  propertyId: string,
  token: string
): Promise<{
  thisWeek: {
    views: number
    sessions: number
    bounceRate: number
    avgSessionDuration: number
    top_pages: Array<{ page: string; page_views: number }>
    top_referrers: Array<{ referrer: string; count: number }>
    conversion_rate?: number
  }
  lastWeek: {
    views: number
    sessions: number
    bounceRate: number
    avgSessionDuration: number
  }
  change: {
    views: number
    sessions: number
    bounceRate: number
    avgSessionDuration: number
  }
}> => {
  const endpoint = `metrics/${propertyId}/weekly-summaries?limit=1`;
  return await getRequest(endpoint, token);
};

/**
 * Get experiment recommendations from AI analysis
 * Leverages the property onboarding analysis that generates split test recommendations
 */
const getExperimentRecommendations = async (
  propertyId: string,
  token: string,
  domain: string
): Promise<{
  recommendations: Array<{
    title: string
    category: string
    hypothesis: string
    control?: string
    variant?: string
    expectedImpact: string
    confidence?: string
    priority_score?: number
    pageUrl?: string
    pageType?: string
    specialist?: string
  }>
  totalGenerated: number
  aggregatedInsights?: {
    optimization_score?: number
    overall_assessment?: string
    key_themes?: string[]
    quick_wins?: string[]
  }
}> => {
  if (!domain) {
    throw new Error("Property domain is required to generate recommendations");
  }

  const analyzeEndpoint = `analyze/${propertyId}`;
  const payload = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      url: `https://${domain}`,
      domain,
    }),
  };

  const response = await fetch(API_URL + analyzeEndpoint, payload);
  if (!response.ok) throw new Error("Failed to get experiment recommendations");
  const { analysisId } = await response.json();
  const analysisResult = await pollAnalysis(propertyId, analysisId, token);

  // Extract test recommendations from specialist analyses
  const recommendations: any[] = [];
  if (analysisResult.page_results) {
    for (const page of analysisResult.page_results) {
      for (const specialist of page.specialist_analyses || []) {
        for (const test of specialist.tests || []) {
          recommendations.push({
            ...test,
            pageUrl: page.page_url,
            pageType: page.page_type,
            specialist: specialist.specialist,
          });
        }
      }
    }
  } else if (analysisResult.specialists) {
    // Legacy single-page format
    for (const specialist of analysisResult.specialists) {
      for (const test of specialist.tests || []) {
        recommendations.push({
          ...test,
          specialist: specialist.specialist,
        });
      }
    }
  }

  return {
    recommendations,
    totalGenerated: recommendations.length,
    aggregatedInsights: analysisResult.aggregated_insights,
  };
};

/**
 * Send account deletion request
 */
export const sendDeleteAccountRequest = async (
  token: string,
  deleteAccountReason: string
) => {
  const endpoint = "delete-account-request";
  const data = {
    reason: deleteAccountReason,
  };
  const payload = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify(data),
  };
  const response = await fetch(API_URL + endpoint, payload);
  // Callers show "Deletion requested" on resolve, so a rejected request must throw.
  if (!response.ok) throw await createApiError(response);
};

/**
 * Activate free trial for user
 */
export const activateTrial = async (token: string) => {
  const endpoint = "billing/activate-trial";
  const payload = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
    credentials: "include" as RequestCredentials,
  };
  const response = await fetch(API_URL + endpoint, payload);
  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ message: "Failed to activate trial" }));
    throw new Error(error.message || "Failed to activate trial");
  }
  return await response.json();
};

/**
 * Send feedback (used by account deletion)
 */
export const sendFeedback = async (message: string, token: string) => {
  const endpoint = "feedback";
  const data = {
    message,
  };
  const payload = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify(data),
  };
  await fetch(API_URL + endpoint, payload);
};

// Team Management API Functions

/**
 * Accept an account invite
 */
export const acceptAccountInvite = async (
  inviteToken: string,
  token: string
) => {
  const endpoint = `invites/${inviteToken}/accept`;
  const payload = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    credentials: "include" as RequestCredentials,
  };
  const response = await fetch(API_URL + endpoint, payload);
  if (!response.ok) throw new Error("Failed to accept invite");
  return await response.json();
};

/**
 * Decline an account invite
 */
export const declineAccountInvite = async (
  inviteToken: string,
  token: string
) => {
  const endpoint = `invites/${inviteToken}/decline`;
  const payload = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    credentials: "include" as RequestCredentials,
  };
  const response = await fetch(API_URL + endpoint, payload);
  if (!response.ok) throw new Error("Failed to decline invite");
  return await response.json();
};

/**
 * Invite a user to access the account
 */
export const inviteTeamMember = async (
  email: string,
  propertyAccess: string[],
  token: string
) => {
  const endpoint = "account/invite";
  const payload = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ email, propertyAccess }),
    credentials: "include" as RequestCredentials,
  };
  const response = await fetch(API_URL + endpoint, payload);
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.error || `HTTP error! status: ${response.status}`
    );
  }
  return await response.json();
};

/**
 * Remove a user from the account
 */
export const removeTeamMember = async (userId: string, token: string) => {
  const endpoint = `account/users/${userId}`;
  const payload = {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    credentials: "include" as RequestCredentials,
  };
  const response = await fetch(API_URL + endpoint, payload);
  if (!response.ok) throw new Error("Failed to remove user from account");
  return await response.json();
};

/**
 * Get all users in the account
 */
export const getTeamMembers = async (token: string) => {
  const endpoint = "account/users";
  return await getRequest(endpoint, token);
};

/**
 * Get all pending account invites (for account owners)
 */
export const getPendingInvites = async (token: string) => {
  const endpoint = "account/invites";
  return await getRequest(endpoint, token);
};

/**
 * Get all pending account invites for the current user
 */
export const getIncomingInvites = async (token: string) => {
  const endpoint = "user/invites";
  const response = await getRequest(endpoint, token);
  // The backend returns { invites: [...] } but we want just the array
  return response.invites || response || [];
};

/**
 * Resend an account invite
 */
export const resendInvite = async (inviteId: string, token: string) => {
  const endpoint = `account/invites/${inviteId}/resend`;
  const payload = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    credentials: "include" as RequestCredentials,
  };
  const response = await fetch(API_URL + endpoint, payload);
  if (!response.ok) throw new Error("Failed to resend invite");
  return await response.json();
};

/**
 * Cancel a pending account invite
 */
export const cancelInvite = async (inviteId: string, token: string) => {
  const endpoint = `account/invites/${inviteId}`;
  const payload = {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    credentials: "include" as RequestCredentials,
  };
  const response = await fetch(API_URL + endpoint, payload);
  if (!response.ok) throw new Error("Failed to cancel invite");
  return await response.json();
};

/**
 * Get account information including subscription details
 * Note: Account info is included in getAllProperties response as 'subscription'.
 * This function returns an empty object as fallback since the primary data source
 * is the getAllProperties response.
 */
export const getAccountInfo = async (_token: string) => {
  // Account info (subscription, team limits, features) comes from getAllProperties response
  // This function is kept for backward compatibility but returns empty object
  return Promise.resolve({});
};

// Site settings API Functions

/**
 * Verify script installation on site
 */
export const verify = async (
  domain: string,
  scriptId: string,
  token: string,
  alternativeUrl?: string
) => {
  const endpoint = "validate-setup-v2";

  const data: any = { domain, scriptId };
  if (alternativeUrl) {
    data.alternativeUrl = alternativeUrl;
  }

  const payload = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify(data),
  };

  const response = await fetch(API_URL + endpoint, payload);
  const result = await response.json();

  if (result?.scriptInstalled === false || result?.scriptInstalledHead === false) {
    try {
      reportOnboardingScriptNotDetected({
        domain,
        scriptId,
        scriptInstalled: result.scriptInstalled,
        scriptInstalledHead: result.scriptInstalledHead,
        alternativeUrl,
      });
    } catch {
      // Observability should never block the verification flow.
    }
  }

  return result;
};

/**
 * Add a custom site/property after verification
 */
const generateCustomSite = async (
  name: string,
  domain: string,
  token: string,
  onboarding: boolean = true
) => {
  const endpoint = "add-custom-site";

  const payload = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify({
      name,
      domain,
      onboarding,
    }),
  };

  const response = await fetch(API_URL + endpoint, payload);
  const result = await response.json();

  return result;
};

/**
 * Update site timezone
 */
const updateTimezone = async (
  propertyId: string,
  timezone: string,
  token: string
) => {
  const endpoint = "property/timezone";
  const payload = {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify({ propertyId, timezone }),
  };
  const response = await fetch(API_URL + endpoint, payload);
  if (!response.ok) throw new Error("Failed to update timezone");
  return await response.json();
};

/**
 * Update revenue settings for site
 */
const updateRevenueSetting = async (
  propertyId: string,
  provider: string,
  settings: any,
  token: string
) => {
  const endpoint = "property/revenue";
  const payload = {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify({ propertyId, provider, settings }),
  };
  const response = await fetch(API_URL + endpoint, payload);
  if (!response.ok) throw new Error("Failed to update revenue settings");
  return await response.json();
};

/**
 * Get revenue configuration for a property
 */
export const getRevenueConfig = async (propertyId: string, token: string) => {
  // The demo property (`00000`) has no backend revenue-config. Hitting the
  // real endpoint returns 403 and Connectors reports it as an Issue.
  if (isDemoSite(propertyId)) {
    return {
      revenueSetup: {
        stripe: {
          configured: false,
        },
        foxy: {
          configured: false,
        },
      },
    };
  }

  const endpoint = `properties/${propertyId}/revenue-config`;
  return await getRequest(endpoint, token);
};

/**
 * Revenue connector configuration payload
 */
type RevenueConfigInput = {
  method?: string;
  apiKey?: string;
  storeUrl?: string;
  encryptionKey?: string;
};

/**
 * Save revenue configuration for a property
 */
export const saveRevenueConfig = async (
  propertyId: string,
  provider: string,
  config: RevenueConfigInput,
  token: string
) => {
  const endpoint = `properties/${propertyId}/revenue-config`;

  const payload = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      provider,
      config,
    }),
    credentials: "include" as RequestCredentials,
  };

  const response = await fetch(API_URL + endpoint, payload);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error || `HTTP error! status: ${response.status}`
    );
  }
  return await response.json();
};

/**
 * Test revenue configuration for a property
 */
export const testRevenueConfig = async (
  propertyId: string,
  provider: string,
  token: string,
  config?: RevenueConfigInput
) => {
  const endpoint = `properties/${propertyId}/revenue-config/test`;
  const hasProvidedCredentials = Boolean(
    config?.apiKey?.trim() ||
      config?.storeUrl?.trim() ||
      config?.encryptionKey?.trim()
  );

  const body = {
    provider,
    ...(hasProvidedCredentials && config ? { config } : {}),
  };

  const payload = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
    credentials: "include" as RequestCredentials,
  };

  // The /test endpoint can intermittently fail validation (e.g. a false
  // "Invalid API key format" error) even for a valid key. Retry a few times
  // with a delay before surfacing the failure to the user.
  const maxAttempts = 3;
  const retryDelayMs = 5000;
  let lastError: unknown;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const response = await fetch(API_URL + endpoint, payload);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || `HTTP error! status: ${response.status}`
        );
      }

      const result = await response.json();

      // A successful HTTP response can still report a failed test. Retry those
      // too, since the underlying flakiness surfaces the same way.
      if (result?.success === false && attempt < maxAttempts) {
        lastError = new Error(result.message || "Connection test failed");
        await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
        continue;
      }

      return result;
    } catch (error) {
      lastError = error;
      if (attempt < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
        continue;
      }
      throw error;
    }
  }

  throw lastError instanceof Error
    ? lastError
    : new Error("Connection test failed");
};

/**
 * Check if a property has Stripe integration configured
 */
const hasStripeIntegration = async (
  propertyId: string,
  token: string
): Promise<boolean> => {
  try {
    const revenueConfig = await getRevenueConfig(propertyId, token);
    return revenueConfig?.revenueSetup?.stripe?.configured === true;
  } catch (error) {
    console.error("Error checking Stripe integration:", error);
    return false;
  }
};

/**
 * Check if a property has Foxy integration configured
 */
const hasFoxyIntegration = async (
  propertyId: string,
  token: string
): Promise<boolean> => {
  try {
    const revenueConfig = await getRevenueConfig(propertyId, token);
    return revenueConfig?.revenueSetup?.foxy?.configured === true;
  } catch (error) {
    console.error("Error checking Foxy integration:", error);
    return false;
  }
};

/**
 * Check if a property has any revenue integration configured (Stripe or Foxy)
 */
const hasRevenueIntegration = async (
  propertyId: string,
  token: string
): Promise<boolean> => {
  try {
    const [stripe, foxy] = await Promise.all([
      hasStripeIntegration(propertyId, token),
      hasFoxyIntegration(propertyId, token),
    ]);
    return stripe || foxy;
  } catch (error) {
    console.error("Error checking revenue integrations:", error);
    return false;
  }
};

/**
 * Get Foxy webhook info for a property
 */
export const getFoxyWebhookInfo = async (propertyId: string, token: string) => {
  const endpoint = `foxy/webhook-info?propertyId=${propertyId}`;
  return await getRequest(endpoint, token);
};

/**
 * Get JotForm webhook info for a property
 */
export const getJotformWebhookInfo = async (propertyId: string, token: string) => {
  const endpoint = `jotform/webhook-info?propertyId=${propertyId}`;
  return await getRequest(endpoint, token);
};

/**
 * Get Typeform webhook info for a property
 */
export const getTypeformWebhookInfo = async (propertyId: string, token: string) => {
  const endpoint = `typeform/webhook-info?propertyId=${propertyId}`;
  return await getRequest(endpoint, token);
};

/**
 * Save Typeform API token for a property (auto-registers webhook)
 */
export const saveTypeformConfig = async (
  propertyId: string,
  apiToken: string,
  token: string
) => {
  const payload = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    credentials: "include" as RequestCredentials,
    body: JSON.stringify({ propertyId, apiToken }),
  };
  const response = await fetch(`${API_URL}typeform/connect`, payload);
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: "Failed to save Typeform config" }));
    throw new Error(error.message || "Failed to save Typeform config");
  }
  return await response.json();
};

/**
 * Test Typeform connection for a property
 */
export const testTypeformConfig = async (propertyId: string, token: string) => {
  const endpoint = `typeform/test?propertyId=${propertyId}`;
  return await getRequest(endpoint, token);
};

/**
 * Get Tally webhook info for a property
 */
export const getTallyWebhookInfo = async (propertyId: string, token: string) => {
  const endpoint = `tally/webhook-info?propertyId=${propertyId}`;
  return await getRequest(endpoint, token);
};

/**
 * Get Webflow webhook info for a property
 */
export const getWebflowWebhookInfo = async (propertyId: string, token: string) => {
  const endpoint = `webflow/webhook-info?propertyId=${propertyId}`;
  return await getRequest(endpoint, token);
};

/**
 * Save Webflow config for a property (site ID + token, auto-registers webhook)
 */
export const saveWebflowConfig = async (
  propertyId: string,
  siteId: string,
  apiToken: string,
  token: string
) => {
  const payload = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    credentials: "include" as RequestCredentials,
    body: JSON.stringify({ propertyId, siteId, apiToken }),
  };
  const response = await fetch(`${API_URL}webflow/connect`, payload);
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: "Failed to save Webflow config" }));
    throw new Error(error.message || "Failed to save Webflow config");
  }
  return await response.json();
};

/**
 * Test Webflow connection for a property
 */
export const testWebflowConfig = async (propertyId: string, token: string) => {
  const endpoint = `webflow/test?propertyId=${propertyId}`;
  return await getRequest(endpoint, token);
};

/**
 * Disconnect/remove revenue configuration for a property
 */
export const disconnectRevenueConfig = async (
  propertyId: string,
  provider: string,
  token: string
) => {
  const endpoint = `properties/${propertyId}/revenue-config`;

  const payload = {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      provider,
    }),
    credentials: "include" as RequestCredentials,
  };

  const response = await fetch(API_URL + endpoint, payload);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error || `HTTP error! status: ${response.status}`
    );
  }
  return await response.json();
};

/**
 * Toggle revenue display for a specific site
 * Uses the revenue-attribution-display endpoint with POST method
 */
export const toggleSiteRevenueDisplay = async (
  propertyId: string,
  enabled: boolean,
  token: string
) => {
  const endpoint = `properties/${propertyId}/revenue-attribution-display`;

  const payload = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      showRevenueAttribution: enabled,
    }),
    credentials: "include" as RequestCredentials,
  };

  const response = await fetch(API_URL + endpoint, payload);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error || `HTTP error! status: ${response.status}`
    );
  }
  return await response.json();
};

/**
 * Get revenue display setting for a specific site
 * This is included in the getRevenueConfig response as showRevenueAttribution
 */
const getSiteRevenueDisplay = async (
  propertyId: string,
  token: string
): Promise<{ enabled: boolean }> => {
  // The revenue display setting is returned as part of the revenue config
  const config = await getRevenueConfig(propertyId, token);
  return { enabled: config?.showRevenueAttribution ?? false };
};

/**
 * Add IP to filter list
 * @deprecated Use updateIPFilters with the full updated array instead
 */
const addIPFilter = async (
  propertyId: string,
  ipAddress: string,
  token: string
) => {
  const endpoint = "property/ip-filter/add";
  const payload = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify({ propertyId, ipAddress }),
  };
  const response = await fetch(API_URL + endpoint, payload);
  if (!response.ok) throw new Error("Failed to add IP filter");
  return await response.json();
};

/**
 * Remove IP from filter list
 * @deprecated Use updateIPFilters with the full updated array instead
 */
const removeIPFilter = async (
  propertyId: string,
  ipAddress: string,
  token: string
) => {
  const endpoint = "property/ip-filter/remove";
  const payload = {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify({ propertyId, ipAddress }),
  };
  const response = await fetch(API_URL + endpoint, payload);
  if (!response.ok) throw new Error("Failed to remove IP filter");
  return await response.json();
};

/**
 * NOTE: Share link (Magic Link) functions have been moved to @/lib/api/share.ts
 *
 * Use the following imports instead:
 * - createMagicLink() - Create a new share link
 * - getMagicLinks() - Get all share links for user
 * - revokeMagicLink() - Revoke a share link
 * - validateMagicLink() - Validate and get JWT token
 */

/**
 * Update email notification settings
 */
const updateNotifications = async (
  propertyId: string,
  settings: {
    weeklyReports?: boolean;
    monthlyReports?: boolean;
    trafficSpikes?: boolean;
    spikeThreshold?: number;
  },
  token: string
) => {
  const endpoint = "property/notifications";
  const payload = {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify({ propertyId, ...settings }),
  };
  const response = await fetch(API_URL + endpoint, payload);
  if (!response.ok) throw new Error("Failed to update notifications");
  return await response.json();
};

/**
 * Update whitelisted domains
 */
const updateWhitelistedDomains = async (
  propertyId: string,
  domains: string[],
  token: string
) => {
  const endpoint = "property/whitelisted-domains";
  const payload = {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify({ propertyId, domains }),
  };
  const response = await fetch(API_URL + endpoint, payload);
  if (!response.ok) throw new Error("Failed to update whitelisted domains");
  return await response.json();
};

export const archiveProperty = async (propertyId: string, token: string) => {
  const endpoint = `archive-property`;
  const payload = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
    credentials: "include" as RequestCredentials,
    body: JSON.stringify({ propertyId }),
  };
  const response = await fetch(API_URL + endpoint, payload);
  if (!response.ok) throw new Error("Failed to archive property");
  return response.ok;
};

/**
 * Get hash fragment tracking enabled status for a property
 */
export const getHashFragmentTrackingEnabled = async (
  propertyId: string,
  token: string
) => {
  const endpoint = `property/${encodeURIComponent(
    propertyId
  )}/hash-fragment-tracking-enabled`;
  return await getRequest(endpoint, token);
};

/**
 * Update hash fragment tracking enabled status for a property
 */
export const updateHashFragmentTrackingEnabled = async (
  propertyId: string,
  enabled: boolean,
  token: string
) => {
  const endpoint = `property/${encodeURIComponent(
    propertyId
  )}/hash-fragment-tracking-enabled`;
  const payload = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify({ enabled }),
  };
  const response = await fetch(API_URL + endpoint, payload);
  if (!response.ok) throw new Error("Failed to update hash fragment tracking");
  const data = await response.json();
  return { hashFragmentTrackingEnabled: data.trackFragments || false };
};

/**
 * Get notification preferences for a property
 */
export const getNotificationPreferences = async (
  propertyId: string,
  token: string
) => {
  const endpoint = `notification-preferences/${encodeURIComponent(propertyId)}`;
  return await getRequest(endpoint, token);
};

/**
 * Update notification preferences for a property
 */
export const updateNotificationPreferences = async (
  propertyId: string,
  preferences: {
    weeklyReport: { enabled: boolean; emails: string[] };
    monthlyReport: { enabled: boolean; emails: string[] };
    trafficSpike: { enabled: boolean; emails: string[] };
    trafficDrop: { enabled: boolean; emails: string[] };
    splitTestActivity: { enabled: boolean; emails: string[] };
  },
  token: string
) => {
  const endpoint = `notification-preferences`;
  const payload = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify({ preferences, propertyId }),
  };
  const response = await fetch(API_URL + endpoint, payload);
  if (!response.ok)
    throw new Error("Failed to update notification preferences");
  return await response.json();
};

/**
 * Get list of IP addresses in filter list for a property
 */
export const getIPFilters = async (propertyId: string, token: string) => {
  // IP filters are returned as part of property data
  const data = await getAllProperties(token);
  const property = data?.properties?.find(
    (p: any) => p.propertyId === propertyId
  );
  return property?.subscription?.ignoreIpAddresses || [];
};

// ============================================================================
// Workspace API Functions
// ============================================================================

export interface Workspace {
  workspace_id: string;
  owner_user_id: string;
  name: string;
  description?: string;
  created_at: string;
  archived: boolean;
  isOwner: boolean;
}

/**
 * Get all workspaces for the current user
 */
export const getUserWorkspaces = async (token: string) => {
  const endpoint = "workspaces";
  return await getRequest(endpoint, token);
};

/**
 * Get the script ID of the workspace owner
 */
export const getWorkspaceOwnerScriptId = async (
  workspaceId: string,
  token: string
) => {
  const endpoint = `workspaces/${workspaceId}/owner-script-id`;
  const payload = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    credentials: "include" as RequestCredentials,
  };

  try {
    const response = await fetch(API_URL + endpoint, payload);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error getting workspace owner script ID:", error);
    throw error;
  }
};

/**
 * Add a custom site to a workspace
 */
/**
 * Whether a domain loads before it is added (POST /api/v1/check-site).
 * `reachable: false` only for a 404/410, a server error or no answer; bot
 * walls (401/403/429) count as reachable.
 */
export const checkSite = async (
  domain: string,
  token: string
): Promise<{ domain: string; reachable: boolean; status: number | null }> => {
  const response = await fetch(API_URL + "check-site", {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    credentials: "include",
    body: JSON.stringify({ domain }),
  });
  if (!response.ok) throw await createApiError(response);
  return response.json();
};

export const addCustomSiteToWorkspace = async (
  workspaceId: string,
  domain: string,
  displayName: string,
  isFramer: boolean,
  token: string,
  onboarding: boolean = true,
  skipVerification: boolean = false
) => {
  const endpoint = `workspaces/${workspaceId}/add-custom-site`;
  const payload = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    credentials: "include" as RequestCredentials,
    body: JSON.stringify({ domain, displayName, isFramer, onboarding, skipVerification }),
  };

  try {
    const response = await fetch(API_URL + endpoint, payload);
    // ApiError keeps the status and body so callers can tell a plan limit or a
    // validation message from an outage (getUserFacingApiError).
    if (!response.ok) throw await createApiError(response);
    return await response.json();
  } catch (error) {
    console.error("Error adding custom site to workspace:", error);
    throw error;
  }
};

// ============================================================================
// AB Test Draft API Functions (No-Code Component Testing)
// ============================================================================

export interface ComponentInstruction {
  id: string;
  selector: string;
  operation:
    | "text"
    | "css"
    | "html"
    | "image"
    | "visibility"
    | "reorder"
    | "attribute"
    | "animation"
    | "custom_js"
    | "custom_css"
    | "class"
    | "form_field"
    | "duplicate"
    | "delete";
  value: any;
  previousValue?: any;
  elementTag?: string;
  elementText?: string;
  elementPath?: string[];
  aiGenerated?: boolean;
  aiReasoning?: string;
  aiConfidence?: number;
  deviceTargeting?: "all" | "mobile" | "tablet" | "desktop";
  timestamp?: string;
  lastModified?: string;
  groupId?: string;
  validated?: boolean;
  validationError?: string;
}

export interface AbTestDraft {
  id: string;
  userId: string;
  propertyId: string;
  pageUrl: string;
  variants: {
    [key: string]: {
      instructions: ComponentInstruction[];
      customJs?: string;
      customCss?: string;
    };
  };
  metadata: {
    experimentName?: string;
    deviceTargeting?: string[];
    aiSuggestions?: any[];
    screenshotUrl?: string;
    lastEditedAt?: string;
  };
  createdAt: string;
  updatedAt: string;
}

/**
 * Get an AB test draft by ID
 */
export interface AbTestDraftResponse {
  testId: string;
  variant: string;
  instructions: ComponentInstruction[];
  updatedAt: string;
}

export const getAbTestDraft = async (
  draftId: string,
  token: string
): Promise<{ success: boolean; data: AbTestDraftResponse | null }> => {
  const endpoint = `ab-tests/${draftId}`;
  return await getRequest(endpoint, token) as { success: boolean; data: AbTestDraftResponse | null };
};

/**
 * Update an AB test draft with new instructions
 */
export const updateAbTestDraft = async (
  draftId: string,
  variant: string,
  instructions: ComponentInstruction[],
  metadata: Partial<AbTestDraft["metadata"]>,
  token: string
): Promise<{ success: boolean; data: AbTestDraft }> => {
  const endpoint = `ab-tests/${draftId}`;
  const payload = {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify({ variant, instructions }),
  };
  const response = await fetch(API_URL + endpoint, payload);
  if (!response.ok) throw new Error("Failed to update AB test draft");
  return await response.json();
};

/**
 * Delete an AB test draft
 */
const deleteAbTestDraft = async (
  draftId: string,
  token: string
): Promise<{ success: boolean }> => {
  const endpoint = `ab-tests/draft/${draftId}`;
  const payload = {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
  };
  const response = await fetch(API_URL + endpoint, payload);
  if (!response.ok) throw new Error("Failed to delete AB test draft");
  return await response.json();
};

/**
 * Get all AB test drafts for a property
 */
const getAbTestDrafts = async (
  propertyId: string,
  token: string
): Promise<{ success: boolean; data: AbTestDraft[] }> => {
  const endpoint = `ab-tests/drafts/${propertyId}`;
  return await getRequest(endpoint, token);
};

// ============================================================================
// AI-Powered Component Testing API Functions
// ============================================================================

export interface PageAnalysisResult {
  highImpactElements: Array<{
    selector: string;
    elementType: string;
    text?: string;
    impactScore: number;
    suggestedTests: string[];
    reasoning: string;
  }>;
  pageInsights: {
    conversionOpportunities: string[];
    uxIssues: string[];
    accessibilityIssues: string[];
  };
}

export interface VariationSuggestion {
  variationType: "text" | "css" | "html" | "attributes";
  changes: any;
  reasoning: string;
  expectedImpact: string;
  confidence: number;
}

export interface CopyOptimizationResult {
  variations: Array<{
    text: string;
    tone: string;
    reasoning: string;
    expectedImpact: string;
  }>;
}

export interface DesignSuggestion {
  property: string;
  currentValue: string;
  suggestedValue: string;
  reasoning: string;
  category: "color" | "spacing" | "typography" | "layout" | "accessibility";
}

export interface CompetitorAnalysisResult {
  patterns: Array<{
    element: string;
    pattern: string;
    frequency: number;
    effectiveness: string;
  }>;
  recommendations: Array<{
    suggestion: string;
    priority: "high" | "medium" | "low";
    reasoning: string;
  }>;
}

/**
 * Analyze a page for high-impact testable elements using AI
 */
const analyzePageForTesting = async (
  pageUrl: string,
  propertyId: string,
  token: string
): Promise<{ success: boolean; data: PageAnalysisResult }> => {
  const endpoint = "ai/analyze-page";
  const payload = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify({ pageUrl, propertyId }),
  };
  const response = await fetch(API_URL + endpoint, payload);
  if (!response.ok) throw new Error("Failed to analyze page");
  return await response.json();
};

/**
 * Generate AI-powered variations for a selected element
 */
export const generateVariation = async (
  elementData: {
    selector: string;
    html: string;
    text?: string;
    styles?: Record<string, string>;
    context?: string;
  },
  variationType: "text" | "css" | "html" | "attributes",
  prompt: string,
  token: string
): Promise<{ success: boolean; data: VariationSuggestion }> => {
  const endpoint = "ai/generate-variation";
  const payload = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify({ elementData, variationType, prompt }),
  };
  const response = await fetch(API_URL + endpoint, payload);
  if (!response.ok) throw new Error("Failed to generate variation");
  return await response.json();
};

/**
 * Optimize copy/text using AI
 */
const optimizeCopy = async (
  text: string,
  context: {
    elementType: string;
    pageUrl: string;
    businessContext?: string;
    targetAudience?: string;
    tone?: string;
  },
  token: string
): Promise<{ success: boolean; data: CopyOptimizationResult }> => {
  const endpoint = "ai/optimize-copy";
  const payload = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify({ text, context }),
  };
  const response = await fetch(API_URL + endpoint, payload);
  if (!response.ok) throw new Error("Failed to optimize copy");
  return await response.json();
};

/**
 * Get AI-powered design improvement suggestions
 */
const suggestDesignImprovements = async (
  elementData: {
    selector: string;
    html: string;
    computedStyles: Record<string, string>;
    boundingRect?: DOMRect;
  },
  token: string
): Promise<{ success: boolean; data: DesignSuggestion[] }> => {
  const endpoint = "ai/suggest-design";
  const payload = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify({ elementData }),
  };
  const response = await fetch(API_URL + endpoint, payload);
  if (!response.ok) throw new Error("Failed to get design suggestions");
  return await response.json();
};

/**
 * Analyze competitor pages for winning patterns
 */
const analyzeCompetitor = async (
  competitorUrl: string,
  focusAreas: string[],
  token: string
): Promise<{ success: boolean; data: CompetitorAnalysisResult }> => {
  const endpoint = "ai/analyze-competitor";
  const payload = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify({ competitorUrl, focusAreas }),
  };
  const response = await fetch(API_URL + endpoint, payload);
  if (!response.ok) throw new Error("Failed to analyze competitor");
  return await response.json();
};

/**
 * Chat with AI assistant for component testing guidance
 * Note: This uses a simpler string-only message format.
 * TODO: Update to support ContentBlock[] when backend endpoint supports tool call preservation
 */
const chatWithAI = async (
  messages: Array<{ role: "user" | "assistant" | "system"; content: string }>,
  context: {
    pageUrl?: string;
    selectedElement?: any;
    currentInstructions?: ComponentInstruction[];
  },
  workflow: "general" | "generate_variation" | "analyze_page" | "optimize_copy",
  token: string
): Promise<{
  success: boolean;
  data: { response: string; toolResults?: any };
}> => {
  const endpoint = "ai/chat";
  
  // Import the system prompt builder dynamically to avoid circular deps
  const { buildSystemPrompt } = await import("@/lib/ai/systemPrompts");
  
  // Determine page context based on workflow
  const pageContextMap: Record<string, string> = {
    generate_variation: "componentEditor",
    analyze_page: "heatmaps",
    optimize_copy: "experiments",
    general: "",
  };
  
  // Build intelligent system prompt with context-aware expertise
  const systemPrompt = buildSystemPrompt({
    pageContext: pageContextMap[workflow],
    hasData: true,
    capabilities: {
      canApplyInstructions: workflow === "generate_variation",
      canCreateExperiments: true,
      canCreateFunnels: true,
    },
  });

  const payload = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify({ messages, context, workflow, systemPrompt }),
  };
  const response = await fetch(API_URL + endpoint, payload);
  if (!response.ok) throw new Error("Failed to chat with AI");
  return await response.json();
};

/**
 * Generate AI image for image testing
 */
const generateAIImage = async (
  prompt: string,
  style: string,
  dimensions: { width: number; height: number },
  token: string
): Promise<{ success: boolean; data: { imageUrl: string } }> => {
  const endpoint = "ai/generate-image";
  const payload = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify({ prompt, style, dimensions }),
  };
  const response = await fetch(API_URL + endpoint, payload);
  if (!response.ok) throw new Error("Failed to generate image");
  return await response.json();
};

/**
 * Verify script installation with optional alternative URL
 */
const verifyWithAlternativeUrl = async (
  domain: string,
  scriptId: string,
  token: string,
  alternativeUrl?: string
) => {
  let endpoint = `verify?domain=${domain}&scriptId=${scriptId}`;
  if (alternativeUrl) {
    endpoint += `&alternativeUrl=${encodeURIComponent(alternativeUrl)}`;
  }
  return await getRequest(endpoint, token);
};

// ============================================================================
// Property Onboarding API Functions
// ============================================================================

export interface OnboardingMessage {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  createdAt?: Date;
  uiComponents?: any[];
}

export interface OnboardingAnalyses {
  version: number;
  completedAt: string;
  durationMs: number;
  property: {
    id: string;
    domain: string;
    name: string;
  };
  crawl: {
    totalPages: number;
    successfulPages: number;
    pages: Array<{
      url: string;
      type: string;
      title?: string;
      priorityScore?: number;
    }>;
  };
  heatmaps: {
    pagesTracked: number;
    pages: Array<{
      url: string;
      pageType: string;
      success: boolean;
      heatmapId?: string;
      error?: string;
    }>;
  };
  splitTests: {
    recommendations: Array<{
      title: string;
      category: string;
      hypothesis: string;
      control?: string;
      variant?: string;
      expectedImpact: string;
      confidence?: string;
      priority_score?: number;
      pageUrl?: string;
      pageType?: string;
      specialist?: string;
    }>;
    totalGenerated: number;
    aggregatedInsights?: {
      optimization_score?: number;
      overall_assessment?: string;
      key_themes?: string[];
      quick_wins?: string[];
    };
    crossPageInsights?: {
      consistency_score?: number;
      user_flow_assessment?: string;
      recommendations?: string[];
    };
    error?: string;
  };
  funnels: {
    suggestions: Array<{
      name: string;
      description: string;
      steps: Array<{
        type: string;
        value: string;
        label: string;
      }>;
      priority: string;
    }>;
    pagesAnalyzed: number;
  };
  summary: {
    pagesDiscovered: number;
    heatmapsSetup: number;
    splitTestRecommendations: number;
    funnelSuggestions: number;
    overallScore?: number;
  };
  error?: boolean;
  errorMessage?: string;
}

export interface OnboardingData {
  status: "pending" | "running" | "completed" | "failed" | "skipped";
  analyses: OnboardingAnalyses | null;
  messages?: OnboardingMessage[];
}

/**
 * Trigger onboarding workflow for a property
 * This runs in the background and analyzes the site, sets up heatmaps, and generates recommendations.
 */
const triggerPropertyOnboarding = async (
  propertyId: string,
  token: string
): Promise<{ message: string; status: string; propertyId: string }> => {
  const endpoint = `property/${propertyId}/onboarding`;
  const payload = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    credentials: "include" as RequestCredentials,
  };

  const response = await fetch(API_URL + endpoint, payload);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error || `Failed to trigger onboarding: ${response.status}`
    );
  }
  return await response.json();
};

/**
 * Get onboarding status and results for a property
 */
const getPropertyOnboarding = async (
  propertyId: string,
  token: string
): Promise<OnboardingData> => {
  const endpoint = `property/${propertyId}/onboarding`;
  return await getRequest(endpoint, token);
};

/**
 * Get onboarding data formatted as chat messages
 */
const getPropertyOnboardingMessages = async (
  propertyId: string,
  token: string
): Promise<{
  status: string;
  messages: OnboardingMessage[];
}> => {
  const endpoint = `property/${propertyId}/onboarding?format=messages`;
  return await getRequest(endpoint, token);
};

/**
 * Send onboarding complete email manually
 * Triggers the Loops email with onboarding results
 */
const sendPropertyOnboardingEmail = async (
  propertyId: string,
  token: string
): Promise<{ message: string; email: string }> => {
  const endpoint = `property/${propertyId}/onboarding/send-email`;
  const payload = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    credentials: "include" as RequestCredentials,
  };

  const response = await fetch(API_URL + endpoint, payload);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error || `Failed to send onboarding email: ${response.status}`
    );
  }
  return await response.json();
};

export const sendInstallInstructions = async (
  developerEmail: string,
  domain: string | null,
  scriptId: string,
  platform: string,
  platformSteps: string,
  token: string
): Promise<void> => {
  const response = await fetch(`${API_URL}send-install-instructions`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    credentials: "include" as RequestCredentials,
    body: JSON.stringify({ developerEmail, domain, scriptId, platform, platformSteps }),
  });
  if (!response.ok) throw new Error(`send-install-instructions failed: ${response.status}`);
};

export const sendMobileSetupLink = async (
  continueUrl: string,
  token: string
): Promise<void> => {
  const response = await fetch(`${API_URL}send-mobile-setup-link`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    credentials: "include" as RequestCredentials,
    body: JSON.stringify({ continueUrl }),
  });
  if (!response.ok) throw new Error(`send-mobile-setup-link failed: ${response.status}`);
};

/**
 * Canonical-URL resolution fields, added additively by the backend once it
 * follows redirects on the probe. Feature-detected on the client — every field
 * is optional so the editor keeps working against a backend that doesn't yet
 * return them. See docs/TEMP_EDITOR_EMBED_RESOLUTION_CONTRACT.md (hand-off doc).
 */
export type EmbedResolution = {
  /** Fully resolved URL after following redirects (params stripped). */
  finalUrl?: string | null;
  /** Whether any redirect hop occurred. */
  redirected?: boolean;
  /** Convenience: new URL(finalUrl).origin. */
  finalOrigin?: string | null;
};

export type EmbedCheckResult =
  | ({ canEmbed: true; reason?: never } & EmbedResolution)
  | ({ canEmbed: false; reason: string } & EmbedResolution)
  | ({ canEmbed: null; reason: "timeout" | "unreachable" } & EmbedResolution);

/**
 * Ask the backend to probe the given URL's HTTP headers and determine
 * whether it can be embedded in an iframe (checks X-Frame-Options and
 * Content-Security-Policy frame-ancestors).
 */
export const checkEmbedUrl = async (
  url: string,
  token: string
): Promise<EmbedCheckResult> => {
  const response = await fetch(
    `${API_URL}check-embed?url=${encodeURIComponent(url)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      credentials: "include" as RequestCredentials,
    }
  );
  if (!response.ok) {
    throw new Error(`check-embed failed: ${response.status}`);
  }
  return response.json();
};

